import { Auth as Authorization } from 'aws-amplify';
import { API_BASE_URL_V2 } from '../../constants'
import { getMaxRetriesOnErrorConfig } from '../../utils/remoteConfig'
import { store } from '../../redux/store';
import { forceLogout, setCloudAuthStatus } from '../../redux/actions/appAction';
import { leaveBreadcrumb, logError, addMetadata } from '../../utils/tracker';
import { isElectron } from '../../utils/miscUtils';
import { logDesktopAuthEvent } from '../../utils/desktopLog';
import FetchError from '../errors';

// Constants for token retry logic during transient auth errors
// These help handle race conditions where Cognito session may not be
// immediately available after login or during token refresh
const TOKEN_RETRY_MAX_ATTEMPTS = 3;
const TOKEN_RETRY_DELAY_MS = 1000; // 1 second between retries

export const getAuthHeaders = async (auth = true): Promise<Record<string, string>> => {
    if (!auth) {
        return {
            "Content-Type": "application/json",
            "Accept": "*/*"
        }
    }
    const authToken = await getToken();

    return authToken
        ? {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
            "Accept": "*/*"
        }
        : {
            "Content-Type": "application/json",
            "Accept": "*/*"
        };
};

const getIsOnlineNow = (): boolean => {
    try {
        const state: any = store?.getState?.();
        const fromRedux = state?.app?.isOnline;
        if (typeof fromRedux === 'boolean') return fromRedux;
    } catch {
        // ignore
    }
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
            return navigator.onLine;
        }
    } catch {
        // ignore
    }
    return true;
};

const maybeSetElectronCloudAuthInvalid = (reason: 'not_authorized' | 'api_401') => {
    if (!isElectron()) return;
    if (!getIsOnlineNow()) return;

    const now = Date.now();
    try {
        const current: any = store?.getState?.()?.app?.cloudAuth;
        const status = current?.status;
        const currentReason = current?.reason;
        const updatedAt = Number(current?.updatedAt || 0);
        const shouldUpdate =
            status !== 'invalid'
            || currentReason !== reason
            || (now - updatedAt) > 30_000;
        if (!shouldUpdate) return;
    } catch {
        // ignore
    }

    store.dispatch(setCloudAuthStatus({
        status: 'invalid',
        reason,
        updatedAt: now,
    }));
};

const getToken = async (retryCount = 0): Promise<string> => {
    try {
        const session: any = await Authorization.currentSession();
        const token = session.accessToken.jwtToken;
        void logDesktopAuthEvent('currentSession.success', {
            retryCount,
            sessionReady: !!token,
        });

        // Log successful token retrieval only if it was a retry
        if (retryCount > 0) {
            leaveBreadcrumb('Auth token retrieved after retry', {
                retryCount
            }, 'state');
        }

        return token;
    } catch (error: any) {
        // Check if this is a Cognito auth error (token revoked, expired, etc.)
        void logDesktopAuthEvent('currentSession.failure', {
            retryCount,
            errorName: error?.name || error?.code,
            errorMessage: error?.message,
        });
        if (error.name === 'NotAuthorizedException' || error.code === 'NotAuthorizedException') {
            console.log('Auth token error:', error.message);

            leaveBreadcrumb('Auth token error', {
                errorName: error.name,
                errorMessage: error.message,
                retryCount
            }, 'error');

            // Check if user recently logged in by checking Redux state
            const loginState = store.getState().login.userLoggedIn;
            const isRecentlyLoggedIn = loginState === "LOGGED_IN";

            // If user is logged in and we haven't exceeded retries, try again
            if (isRecentlyLoggedIn && retryCount < TOKEN_RETRY_MAX_ATTEMPTS) {
                console.log('User is logged in, retrying token fetch (attempt', retryCount + 1, ')');
                leaveBreadcrumb('Retrying auth token fetch', {
                    retryAttempt: retryCount + 1,
                    maxAttempts: TOKEN_RETRY_MAX_ATTEMPTS
                }, 'state');
                await new Promise(resolve => setTimeout(resolve, TOKEN_RETRY_DELAY_MS));
                return getToken(retryCount + 1);
            }

            // Only trigger force logout if retries exhausted
            if (isRecentlyLoggedIn) {
                console.log('Token fetch failed after retries, triggering force logout');
                leaveBreadcrumb('Auth token fetch failed, forcing logout', {
                    retries: retryCount
                }, 'error');

                logError(new Error('Auth token fetch failed after retries'), {
                    context: 'auth_token_fetch',
                    retries: retryCount,
                    loginState
                });
            }
            // Never force-logout while offline; treat auth/token failures as transient.
            // Re-validate auth when we're online again.
            if (getIsOnlineNow()) {
                if (isElectron()) {
                    maybeSetElectronCloudAuthInvalid('not_authorized');
                } else {
                    store.dispatch(forceLogout());
                }
            }
        }
        throw error;
    }
}

export const customFetchWithRetry = async (
    input: RequestInfo,
    init?: RequestInit | undefined,
    auth = true
): Promise<Response> => {
    return customFetchApi(API_BASE_URL_V2()!!, input, init, auth, true, 0)
};

export const customUrlFetchWithRetry = async (
    url: string,
    init?: RequestInit | undefined,
    auth = true
): Promise<Response> => {
    return customFetchApiWithUrl(url, init, auth, true, 0)
};

export const customFetchWithRetryV2 = async (
    input: RequestInfo,
    init?: RequestInit | undefined,
    auth = true
): Promise<Response> => {
    return customFetchApi(API_BASE_URL_V2()!!, input, init, auth, true, 0)
};

export const customFetch = async (
    input: RequestInfo,
    init?: RequestInit | undefined,
    auth = true,
    retry = false,
    retryCount = 0
): Promise<Response> => {
    return customFetchApi(API_BASE_URL_V2()!!, input, init, auth, retry, retryCount)
};

const headersToRecord = (headers?: HeadersInit): Record<string, string> => {
    if (!headers) return {};

    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
        const result: Record<string, string> = {};
        headers.forEach((value: string, key: string) => {
            result[key] = value;
        });
        return result;
    }

    if (Array.isArray(headers)) {
        return headers.reduce<Record<string, string>>((result, [key, value]) => {
            result[key] = value;
            return result;
        }, {});
    }

    return headers as Record<string, string>;
};

const hasHeader = (headers: Record<string, string>, headerName: string): boolean => {
    const targetHeaderName = headerName.toLowerCase();
    return Object.keys(headers).some((key) => key.toLowerCase() === targetHeaderName);
};

const customFetchApi = async (
    baseUrl: string,
    input: RequestInfo,
    init?: RequestInit | undefined,
    auth = true,
    retry = false,
    retryCount = 0
): Promise<Response> => {
    return customFetchApiWithUrl(baseUrl + input, init, auth, retry, retryCount)
};

const customFetchApiWithUrl = async (
    url: string,
    init?: RequestInit | undefined,
    auth = true,
    retry = false,
    retryCount = 0
): Promise<Response> => {
    const appState: any = store.getState()?.app;
    // NetInfo-derived offline detection can be unreliable on Electron/Windows.
    // Never hard-block API calls on desktop; let `fetch` fail naturally if offline.
    if (!isElectron() && appState?.isOnline === false) {
        const method = init?.method || 'GET';
        const endpoint = url.replace(API_BASE_URL_V2() || '', '');
        leaveBreadcrumb('API request blocked (offline)', {
            endpoint,
            method
        }, 'state');
        throw new FetchError('Internet connection required', 'offline');
    }

    const startTime = Date.now();
    const method = init?.method || 'GET';
    const isRetry = retryCount > 0;

    // Extract endpoint name for cleaner breadcrumbs
    const endpoint = url.replace(API_BASE_URL_V2() || '', '');

    // Log request start (only on first attempt, not retries)
    if (!isRetry) {
        leaveBreadcrumb('API request started', {
            endpoint,
            method,
            hasAuth: auth,
            retryEnabled: retry
        }, 'request');
    }

    try {
        const initHeaders = headersToRecord(init?.headers);
        const shouldAddJsonContentType =
            !!init
            && (init.method === "PUT" || init.method === "POST")
            && !hasHeader(initHeaders, "Content-Type");

        const response = await fetch(`${url}`, {
            ...init,
            headers: {
                ...await getAuthHeaders(auth),
                ...(shouldAddJsonContentType && {
                    "Content-Type": "application/json"
                }),
                ...initHeaders,
            }
        });

        const duration = Date.now() - startTime;

        if (response.ok) {
            leaveBreadcrumb('API request succeeded', {
                endpoint,
                method,
                status: response.status,
                duration,
                retryCount
            }, 'request');

            // Track successful API calls metadata (only for non-retries to avoid spam)
            if (!isRetry) {
                addMetadata('lastSuccessfulRequest', {
                    endpoint,
                    method,
                    status: response.status,
                    duration,
                    timestamp: new Date().toISOString()
                });
            }

            return response;
        }

        // Response not OK - log the failure
        leaveBreadcrumb('API request failed', {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            duration,
            retryCount,
            willRetry: retry && retryCount < getMaxRetriesOnErrorConfig()
        }, 'error');

        // Log specific error types
        if (response.status === 401) {
            leaveBreadcrumb('API 401 Unauthorized', {
                endpoint,
                method
            }, 'error');
            if (isElectron()) {
                maybeSetElectronCloudAuthInvalid('api_401');
            }
        } else if (response.status === 403) {
            leaveBreadcrumb('API 403 Forbidden', {
                endpoint,
                method
            }, 'error');
        } else if (response.status === 404) {
            leaveBreadcrumb('API 404 Not Found', {
                endpoint,
                method
            }, 'error');
        } else if (response.status >= 500) {
            leaveBreadcrumb('API 5xx Server Error', {
                endpoint,
                method,
                status: response.status
            }, 'error');

            // Log server errors to Bugsnag
            logError(new Error(`API Server Error: ${response.status} ${response.statusText}`), {
                context: 'api_request',
                endpoint,
                method,
                status: response.status,
                duration,
                retryCount
            });
        }

        // Retry if enabled
        if(retry && retryCount < getMaxRetriesOnErrorConfig()) {
            leaveBreadcrumb('API request retrying', {
                endpoint,
                method,
                retryAttempt: retryCount + 1,
                maxRetries: getMaxRetriesOnErrorConfig()
            }, 'request');
            return customFetchApiWithUrl(url, init, auth, retry, retryCount + 1);
        }

        // Track failed request metadata
        addMetadata('lastFailedRequest', {
            endpoint,
            method,
            status: response.status,
            duration,
            retryCount,
            timestamp: new Date().toISOString()
        });

        return response;
    } catch (error: any) {
        const duration = Date.now() - startTime;

        leaveBreadcrumb('API request network error', {
            endpoint,
            method,
            error: error.message,
            errorName: error.name,
            errorCode: error.code,
            errorType: error.type,
            errorStack: error.stack?.split('\n').slice(0, 3).join('\n'), // First 3 lines of stack
            duration,
            retryCount,
            willRetry: retry && retryCount < getMaxRetriesOnErrorConfig()
        }, 'error');

        // Log network errors to Bugsnag
        logError(error, {
            context: 'api_network_error',
            endpoint,
            method,
            duration,
            retryCount,
            errorMessage: error.message,
            errorName: error.name,
            errorCode: error.code,
            errorType: error.type,
            errorCause: error.cause ? String(error.cause) : undefined
        });

        // if the retryCount has not been exceeded, call again
        if (retry && retryCount < getMaxRetriesOnErrorConfig()) {
            leaveBreadcrumb('API request retrying after network error', {
                endpoint,
                method,
                retryAttempt: retryCount + 1
            }, 'request');
            return customFetchApiWithUrl(url, init, auth, retry, retryCount + 1);
        }

        // Track failed request metadata
        addMetadata('lastFailedRequest', {
            endpoint,
            method,
            error: error.message,
            duration,
            retryCount,
            timestamp: new Date().toISOString()
        });

        // max retries exceeded
        throw error;
    }
};
